import React, { useEffect, useRef, useState } from 'react';
import { Close, Delete } from "@mui/icons-material";
import api from '../../services/api';
import DesenhoGeometriaMapa, { FerramentaDesenho } from '../../components/GeoDraw/DesenhoGeometriaMapa';

const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

const TIPOS: { value: string; label: string }[] = [
  { value: 'bacia', label: 'Bacia' },
  { value: 'rio', label: 'Rio' },
  { value: 'represa', label: 'Represa' },
  { value: 'trecho_municipio', label: 'Trecho por município' },
  { value: 'area_pesca', label: 'Área de pesca específica' },
  { value: 'outro', label: 'Outro' },
];

interface LocalFormProps {
  /** uid do local sendo editado — ausente = criando um novo */
  uidEditando?: string;
  /** pai já pré-definido ao criar um "filho de" a partir da árvore */
  uidPaiInicial?: string;
  /** lista completa (flat) — usada pro select de pai, excluindo o próprio nó e seus descendentes */
  todosOsLocais: any[];
  onClose: () => void;
  onSaved: () => void;
}

const LocalForm: React.FC<LocalFormProps> = ({ uidEditando, uidPaiInicial, todosOsLocais, onClose, onSaved }) => {
  const isEdicao = !!uidEditando;
  const [carregando, setCarregando] = useState(isEdicao);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState('rio');
  const [uidPai, setUidPai] = useState(uidPaiInicial || '');
  const [uf, setUf] = useState('');
  const [idMunicipio, setIdMunicipio] = useState<number | ''>('');
  const [municipioTexto, setMunicipioTexto] = useState('');
  const [sugestoesMunicipio, setSugestoesMunicipio] = useState<any[]>([]);
  const [buscandoMunicipio, setBuscandoMunicipio] = useState(false);
  const municipioDebounce = useRef<any>(null);

  const [aliases, setAliases] = useState<any[]>([]);
  const [novoAlias, setNovoAlias] = useState('');
  const [salvandoAlias, setSalvandoAlias] = useState(false);

  // Geometria — undefined = não mexe no campo (não pode sobrescrever um
  // traçado/polígono que já exista pra esse nó, ex. vindo da importação de
  // dado geográfico, só porque o usuário editou nome/pai por aqui). null =
  // apagada explicitamente pelo usuário. Objeto = geometria nova/editada.
  // Qual ferramenta de desenho fica disponível depende do tipo: bacia é
  // naturalmente um polígono, rio uma linha; represa aceita tanto um ponto
  // simples (reservatório pequeno) quanto um polígono (contorno de
  // verdade); área de pesca/trecho por município continuam só ponto.
  const [geometria, setGeometria] = useState<any | null | undefined>(undefined);
  const ferramentasDesenho: FerramentaDesenho[] = (() => {
    if (tipo === 'bacia') return ['polygon'];
    if (tipo === 'rio') return ['polyline'];
    if (tipo === 'represa') return ['marker', 'polygon'];
    if (tipo === 'area_pesca' || tipo === 'trecho_municipio') return ['marker'];
    return [];
  })();

  useEffect(() => {
    if (!uidEditando) return;
    api.get(`/pesca/locais/${uidEditando}`).then((res) => {
      const d = res.data.data;
      setNome(d.nome || '');
      setTipo(d.tipo || 'rio');
      setUidPai(d.uid_pesca_local_pai || '');
      setUf(d.uf || '');
      setIdMunicipio(d.id_municipio || '');
      setMunicipioTexto(d.municipio || '');
      setAliases(d.aliases || []);
      setGeometria(d.geometria || undefined);
      setCarregando(false);
    }).catch(() => {
      setErro('Não foi possível carregar esse local.');
      setCarregando(false);
    });
  }, [uidEditando]);

  // Opções de pai válidas: qualquer local ativo, exceto o próprio nó e
  // qualquer descendente dele (o backend também bloqueia — isso é só pra
  // não nem oferecer a opção inválida no select).
  const localAtual = todosOsLocais.find((l) => l.uid_pesca_local === uidEditando);
  const prefixoProprio = localAtual ? `${localAtual.path}${localAtual.uid_pesca_local}.` : null;
  const opcoesPai = todosOsLocais.filter((l) => {
    if (uidEditando && l.uid_pesca_local === uidEditando) return false;
    if (prefixoProprio && l.path.startsWith(prefixoProprio)) return false;
    return true;
  });

  const handleBuscarMunicipio = (texto: string) => {
    setMunicipioTexto(texto);
    setIdMunicipio('');
    if (municipioDebounce.current) clearTimeout(municipioDebounce.current);
    if (texto.trim().length < 2) {
      setSugestoesMunicipio([]);
      return;
    }
    municipioDebounce.current = setTimeout(async () => {
      setBuscandoMunicipio(true);
      const params = new URLSearchParams({ search: texto.trim() });
      if (uf) params.set('id_estado', uf);
      const res = await api.get(`/pesca/municipios?${params.toString()}`);
      setSugestoesMunicipio(res.data.data);
      setBuscandoMunicipio(false);
    }, 400);
  };

  const selecionarMunicipio = (m: any) => {
    setIdMunicipio(m.id_municipio);
    setMunicipioTexto(m.municipio);
    if (m.id_estado) setUf(m.id_estado);
    setSugestoesMunicipio([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      setErro('Informe o nome.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      const payload = {
        nome: nome.trim(),
        tipo,
        uid_pesca_local_pai: uidPai || null,
        uf: uf || null,
        id_municipio: idMunicipio || null,
        geometria,
      };
      if (isEdicao) {
        await api.put(`/pesca/locais/${uidEditando}`, payload);
      } else {
        await api.post('/pesca/locais', payload);
      }
      onSaved();
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao salvar local.');
    } finally {
      setSalvando(false);
    }
  };

  const handleAdicionarAlias = async () => {
    if (!novoAlias.trim() || !uidEditando) return;
    setSalvandoAlias(true);
    setErro('');
    try {
      const res = await api.post(`/pesca/locais/${uidEditando}/aliases`, { texto: novoAlias.trim() });
      setAliases((prev) => [...prev, res.data.data]);
      setNovoAlias('');
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao adicionar alias.');
    } finally {
      setSalvandoAlias(false);
    }
  };

  const handleRemoverAlias = async (uidAlias: string) => {
    try {
      await api.delete(`/pesca/locais/aliases/${uidAlias}`);
      setAliases((prev) => prev.filter((a) => a.uid_pesca_local_alias !== uidAlias));
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao remover alias.');
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: 'var(--green-700, #00b37e)', fontSize: '1.2rem' }}>
            {isEdicao ? 'Editar Local' : 'Novo Local'}
          </h2>
          <Close style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        {carregando ? (
          <p>Carregando...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldWrapStyle, flex: 2 }}>
                <label style={labelStyle}>Nome *</label>
                <input style={inputStyle} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Tietê" />
              </div>
              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>Tipo *</label>
                <select style={inputStyle} value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Local pai (deixe em branco pra ser raiz)</label>
              <select style={inputStyle} value={uidPai} onChange={(e) => setUidPai(e.target.value)}>
                <option value="">— raiz —</option>
                {opcoesPai.map((l) => (
                  <option key={l.uid_pesca_local} value={l.uid_pesca_local}>
                    {'—'.repeat(l.path.split('.').filter(Boolean).length)} {l.nome} ({l.tipo})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldWrapStyle, width: '90px' }}>
                <label style={labelStyle}>UF</label>
                <select style={inputStyle} value={uf} onChange={(e) => setUf(e.target.value)}>
                  <option value="">-</option>
                  {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div style={{ ...fieldWrapStyle, flex: 1, position: 'relative' }}>
                <label style={labelStyle}>Município</label>
                <input
                  style={inputStyle}
                  value={municipioTexto}
                  onChange={(e) => handleBuscarMunicipio(e.target.value)}
                  placeholder="Digite pra buscar..."
                  autoComplete="off"
                />
                {(buscandoMunicipio || sugestoesMunicipio.length > 0) && (
                  <div style={suggestionBoxStyle}>
                    {buscandoMunicipio && <div style={{ padding: '8px', color: '#999', fontSize: '0.85rem' }}>Buscando...</div>}
                    {!buscandoMunicipio && sugestoesMunicipio.map((m) => (
                      <div key={m.id_municipio} style={suggestionItemStyle} onClick={() => selecionarMunicipio(m)}>
                        {m.municipio} {m.id_estado ? `- ${m.id_estado}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {ferramentasDesenho.length > 0 && (
              <div style={fieldWrapStyle}>
                <label style={labelStyle}>
                  Geometria (opcional — usada no Mapa de Pesca)
                  {tipo === 'represa' && ' — marque um ponto simples ou desenhe o contorno da represa'}
                  {tipo === 'bacia' && ' — desenhe o contorno da bacia'}
                  {tipo === 'rio' && ' — desenhe o traçado do rio'}
                </label>
                <DesenhoGeometriaMapa
                  geometriaInicial={geometria}
                  ferramentas={ferramentasDesenho}
                  onMudou={setGeometria}
                  altura="260px"
                />
              </div>
            )}

            {isEdicao && (
              <div style={{ borderTop: '1px solid #eee', paddingTop: '12px', marginTop: '8px' }}>
                <label style={{ ...labelStyle, fontWeight: 'bold', color: '#444' }}>
                  Aliases (variações de texto que apontam pra esse local)
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', margin: '8px 0' }}>
                  {aliases.length === 0 && <p style={{ color: '#999', fontSize: '0.82rem', margin: 0 }}>Nenhum alias cadastrado.</p>}
                  {aliases.map((a) => (
                    <div key={a.uid_pesca_local_alias} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f7f7f7', padding: '4px 8px', borderRadius: '6px' }}>
                      <span style={{ fontSize: '0.85rem' }}>{a.texto}</span>
                      <Delete fontSize="small" style={{ color: '#f75a68', cursor: 'pointer' }} onClick={() => handleRemoverAlias(a.uid_pesca_local_alias)} />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    value={novoAlias}
                    onChange={(e) => setNovoAlias(e.target.value)}
                    placeholder="Nova variação de texto (ex: tiete)"
                  />
                  <button type="button" onClick={handleAdicionarAlias} disabled={salvandoAlias || !novoAlias.trim()} style={secondaryBtnStyle}>
                    Adicionar
                  </button>
                </div>
              </div>
            )}

            {erro && <p style={{ color: '#f75a68', fontSize: '0.85rem' }}>{erro}</p>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancelar</button>
              <button type="submit" disabled={salvando} style={primaryBtnStyle}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  width: '90%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
};
const fieldWrapStyle: React.CSSProperties = { marginBottom: '14px' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '4px' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer',
};
const suggestionBoxStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#fff',
  border: '1px solid #ddd', borderRadius: '8px', marginTop: '4px', maxHeight: '160px', overflowY: 'auto',
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
};
const suggestionItemStyle: React.CSSProperties = {
  padding: '8px 10px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0',
};

export default LocalForm;
